import { X } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { Dialog as Primitive } from 'radix-ui'

import { cn } from '@/lib/cn'

import { IconButton } from './IconButton'

/**
 * Flips a class one frame after mount so a CSS transition has two states to run
 * between. Radix unmounts the content immediately on close, so this is an
 * enter-only transition — which is the honest tradeoff for not shipping a
 * keyframe library. The global reduced-motion rule neutralises it.
 */
export function useEnterTransition(open: boolean): boolean {
  const [entered, setEntered] = useState(false)

  useEffect(() => {
    if (!open) {
      setEntered(false)
      return
    }
    const frame = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(frame)
  }, [open])

  return entered
}

export type DialogSize = 'sm' | 'md' | 'lg'

export interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  /** Hides the visible heading but keeps it for assistive tech. */
  hideTitle?: boolean
  size?: DialogSize
  /** Right-aligned action row pinned under the body. */
  footer?: ReactNode
  children?: ReactNode
  className?: string
  /** Optional trigger keeps focus restoration and dialog semantics in Radix. */
  trigger?: ReactNode
}

const SIZES: Record<DialogSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  hideTitle = false,
  size = 'md',
  footer,
  children,
  className,
  trigger,
}: DialogProps) {
  const entered = useEnterTransition(open)

  // Radix warns when a dialog has no description. Explicitly clearing the
  // attribute is its documented way of saying "there is none, on purpose".
  const describedBy: { 'aria-describedby'?: undefined } = description
    ? {}
    : { 'aria-describedby': undefined }

  return (
    <Primitive.Root open={open} onOpenChange={onOpenChange}>
      {trigger ? <Primitive.Trigger asChild>{trigger}</Primitive.Trigger> : null}
      <Primitive.Portal>
        <Primitive.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-overlay transition-opacity duration-150',
            entered ? 'opacity-100' : 'opacity-0',
          )}
        />
        <Primitive.Content
          {...describedBy}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 flex max-h-[85dvh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col',
            'rounded-card border border-border bg-surface-raised shadow-2xl',
            'transition-[opacity,scale] duration-150',
            entered ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
            SIZES[size],
            className,
          )}
        >
          <div className="flex items-start gap-3 px-5 pt-5">
            <div className="min-w-0 flex-1">
              <Primitive.Title
                className={cn(
                  'text-base font-semibold text-text',
                  hideTitle && 'sr-only',
                )}
              >
                {title}
              </Primitive.Title>
              {description ? (
                <Primitive.Description className="mt-1 text-sm text-text-muted">
                  {description}
                </Primitive.Description>
              ) : null}
            </div>
            <Primitive.Close asChild>
              <IconButton aria-label="Close" size="sm" className="-mr-1 -mt-1">
                <X className="h-5 w-5" aria-hidden="true" />
              </IconButton>
            </Primitive.Close>
          </div>

          {children ? (
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          ) : (
            <div className="h-4" />
          )}

          {footer ? (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-3">
              {footer}
            </div>
          ) : null}
        </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  )
}
